import { Link } from "react-router-dom";
import { MdInfoOutline } from "react-icons/md";
import Card from "components/card";
import { formatNumber } from "utils/format";
import { WEEK_DAYS, formatHour } from "./DeliverySection";

const RECIPIENTS_TAB = "recipients";
const DELIVERY_TAB = "delivery";

const CHANNELS = {
  email: "Email",
  sms: "SMS",
  both: "Email + SMS",
};

// The backend stores "auto"; the editor state uses "automatic".
const AUTOMATIC_MODES = ["automatic", "auto"];

const strong = (text) => ({ strong: text });
const missing = (text, tab) => ({ missing: text, tab });

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const positiveInt = (value) => {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n >= 1 ? n : null;
};

const countOf = (ids) => (Array.isArray(ids) ? ids.length : 0);

/**
 * Audience and timing phrases per trigger type (see TriggerConfiguration).
 * Required numbers that are unset render as a link to the Recipients tab.
 */
const TRIGGER_PHRASES = {
  member_creation: () => ({
    audience: [strong("new members")],
    timing: [strong("when they are added")],
  }),
  first_checkin: () => ({
    audience: [strong("people")],
    timing: [strong("after their first check-in")],
  }),
  days_after_joining: ({ delayDays }) => {
    const days = positiveInt(delayDays);
    return {
      audience: [strong("new members")],
      timing: days
        ? [strong(`${plural(days, "day")} after they join`)]
        : [strong("after they join"), " (", missing("choose how many days", RECIPIENTS_TAB), ")"],
    };
  },
  group_joined: ({ groupIds, delayHours }) => {
    const groups = countOf(groupIds);
    const hours = positiveInt(delayHours);
    return {
      audience: ["people who join ", strong(groups ? plural(groups, "selected group") : "any group")],
      timing: [strong(hours ? `${plural(hours, "hour")} later` : "right away")],
    };
  },
  membership_anniversary: ({ yearsInterval }) => {
    const years = positiveInt(yearsInterval);
    return {
      audience: [strong("members")],
      timing:
        years === 1
          ? [strong("on each membership anniversary")]
          : years
          ? [strong(`every ${years} years`), " on their membership anniversary"]
          : [strong("on their membership anniversary"), " (", missing("choose how often", RECIPIENTS_TAB), ")"],
    };
  },
  inactivity: ({ inactivityDays }) => {
    const days = positiveInt(inactivityDays);
    return {
      audience: [strong("members")],
      timing: days
        ? [strong(`after ${plural(days, "day")} without a check-in`)]
        : [strong("after a stretch without check-ins"), " (", missing("choose how many days", RECIPIENTS_TAB), ")"],
    };
  },
  campus_assignment: ({ campusIds }) => {
    const campuses = countOf(campusIds);
    return {
      audience: [
        "people assigned to ",
        strong(campuses ? plural(campuses, "selected campus", "selected campuses") : "any campus"),
      ],
      timing: [strong("right away")],
    };
  },
};

const scheduleDaysLabel = (scheduleDays) => {
  const days = [...new Set(scheduleDays || [])]
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
    .sort((a, b) => a - b);
  if (days.length === 0 || days.length === 7) return "daily";
  const key = days.join(",");
  if (key === "1,2,3,4,5") return "weekdays";
  if (key === "0,6") return "weekends";
  return days.map((d) => WEEK_DAYS[d]).join(", ");
};

const deliveryWindowLabel = ({ scheduleDays, deliveryWindowStart, deliveryWindowEnd }) => {
  const days = scheduleDaysLabel(scheduleDays);
  if (!Number.isInteger(deliveryWindowStart) || !Number.isInteger(deliveryWindowEnd)) {
    return days === "daily" ? null : days;
  }
  return `${days} ${formatHour(deliveryWindowStart)}–${formatHour(deliveryWindowEnd)}`;
};

const hasFilters = (criteria) =>
  !!criteria &&
  (countOf(criteria.campusIds) > 0 ||
    countOf(criteria.groupIds) > 0 ||
    countOf(criteria.tagIds) > 0 ||
    countOf(criteria.roles) > 0 ||
    (!!criteria.surveyStatus && criteria.surveyStatus !== "any") ||
    !!criteria.membershipAgeDays);

/** QR/link campaigns: open access, nothing is sent. */
export const isQrCampaign = (form) =>
  form.deliveryMethod === "qr_link" || form.recipientMode === "manual";

/**
 * Plain-language summary of a campaign editor form.
 *
 * Returns sentence parts: a string is plain text, `{ strong }` is an emphasised
 * value, and `{ missing, tab }` is an unset piece that links to the editor tab
 * where it is chosen.
 *
 * @param {object} form `formData` from useCampaignEditor.
 * @returns {Array<string | {strong: string} | {missing: string, tab: string}>}
 */
export const describeCampaign = (form = {}) => {
  if (isQrCampaign(form)) {
    return [
      strong("QR code"),
      " survey: anyone who scans the code or opens the link can respond. Nothing is sent automatically.",
    ];
  }

  const channel = CHANNELS[form.deliveryMethod]
    ? [strong(CHANNELS[form.deliveryMethod])]
    : ["a survey (", missing("choose a delivery method", DELIVERY_TAB), ")"];

  if (form.recipientMode === "custom") {
    return [strong("Custom"), " campaign: you choose recipients and send ", ...channel, " when ready."];
  }

  if (!AUTOMATIC_MODES.includes(form.recipientMode)) {
    return ["Sends ", ...channel, " once you ", missing("choose recipients", RECIPIENTS_TAB), "."];
  }

  const phrase = TRIGGER_PHRASES[form.triggerType];
  const parts = ["Sends ", ...channel];
  if (phrase) {
    const { audience, timing } = phrase(form.triggerConfig || {});
    parts.push(" to ", ...audience, " ", ...timing);
  } else {
    parts.push(" automatically once you ", missing("choose a trigger", RECIPIENTS_TAB));
  }

  const windowLabel = deliveryWindowLabel(form);
  if (windowLabel) parts.push(", ", strong(windowLabel));
  parts.push(".");
  if (hasFilters(form.filterCriteria)) parts.push(" Only people who match your filters receive it.");
  return parts;
};

/** The summary as plain text. */
export const summaryText = (parts) =>
  parts.map((part) => (typeof part === "string" ? part : part.strong ?? part.missing)).join("");

const renderPart = (part, index) => {
  if (typeof part === "string") return part;
  if (part.strong !== undefined) {
    return (
      <strong key={index} className="font-semibold">
        {part.strong}
      </strong>
    );
  }
  return (
    <Link
      key={index}
      to={`?tab=${part.tab}`}
      replace
      className="focus-ring rounded-sm font-medium text-primary underline underline-offset-2 hover:no-underline dark:text-accent"
    >
      {part.missing}
    </Link>
  );
};

/**
 * CampaignSummary - One-sentence description of what the campaign will do,
 * plus how many people match right now when a count is known.
 *
 * @param {object} props
 * @param {object} props.formData Editor form state.
 * @param {number|null} [props.recipientCount] Live filter count or the cached count.
 */
const CampaignSummary = ({ formData, recipientCount = null }) => {
  const showCount = recipientCount != null && !isQrCampaign(formData);

  return (
    <Card as="section" variant="outlined" padding="sm" aria-label="Campaign summary">
      <div className="flex items-start gap-3">
        <MdInfoOutline aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-primary dark:text-accent" />
        <div className="min-w-0 space-y-1 text-sm">
          <p className="text-fg">{describeCampaign(formData).map(renderPart)}</p>
          {showCount && (
            <p className="text-fg-muted">
              About <strong className="font-semibold text-fg">{formatNumber(recipientCount)}</strong>{" "}
              {recipientCount === 1 ? "person matches" : "people match"} right now.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};

export default CampaignSummary;
